'use client'
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode
} from 'react'
import { usePathname } from 'next/navigation'
import { Globe2 } from 'lucide-react'
import { LOCALE_KEY, resolveLocale, type Locale } from './locale'

const LanguageContext = createContext({ locale: 'en' as Locale, toggleLocale: () => {} })
export const useLanguage = () => useContext(LanguageContext)

export function LanguageProvider({ children }: { children: ReactNode }) {
	const [locale, setLocale] = useState<Locale>('en')
	const pathname = usePathname()
	const translator = useRef<{ setLocale: (value: Locale) => void; disconnect: () => void } | null>(
		null
	)
	const latest = useRef<Locale>('en')
	const persist = useCallback((value: Locale) => {
		latest.current = value
		setLocale(value)
		document.documentElement.lang = value === 'zh' ? 'zh-CN' : 'en'
		document.cookie = `${LOCALE_KEY}=${value}; Path=/; Max-Age=31536000; SameSite=Lax`
		try {
			localStorage.setItem(LOCALE_KEY, value)
		} catch {}
		translator.current?.setLocale(value)
	}, [])
	useEffect(() => {
		const sync = () => {
			let saved = document.cookie
				.split('; ')
				.find((row) => row.startsWith(`${LOCALE_KEY}=`))
				?.split('=')[1]
			if (!saved) {
				try {
					saved = localStorage.getItem(LOCALE_KEY) || undefined
				} catch {}
			}
			persist(resolveLocale(new URLSearchParams(location.search).get('lang'), saved))
		}
		sync()
		window.addEventListener('popstate', sync)
		return () => window.removeEventListener('popstate', sync)
	}, [pathname, persist])
	useEffect(() => {
		if (locale !== 'zh' || translator.current) return
		let cancelled = false
		import('./translate-dom').then(({ createTranslator }) => {
			if (!cancelled) translator.current = createTranslator(document.body, latest.current)
		})
		return () => {
			cancelled = true
		}
	}, [locale])
	useEffect(() => () => translator.current?.disconnect(), [])
	const toggleLocale = useCallback(() => {
		const next = latest.current === 'zh' ? 'en' : 'zh'
		const url = new URL(location.href)
		url.searchParams.set('lang', next)
		window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
		persist(next)
	}, [persist])
	return (
		<LanguageContext.Provider value={{ locale, toggleLocale }}>{children}</LanguageContext.Provider>
	)
}

export function LanguageToggle() {
	const { locale, toggleLocale } = useLanguage()
	return (
		<button
			type="button"
			translate="no"
			onClick={toggleLocale}
			aria-label={locale === 'zh' ? 'Switch to English' : '切换至中文'}
			title={locale === 'zh' ? 'Switch to English' : 'Switch to Chinese'}
			className="inline-flex h-7 shrink-0 items-center gap-2 rounded-full bg-foreground/5 px-3 text-xs font-normal text-foreground transition-colors hover:bg-foreground/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
		>
			<Globe2 className="size-3.5" aria-hidden="true" />
			<span>{locale === 'zh' ? '简体中文' : 'English'}</span>
			<span className="hidden text-muted-foreground lg:inline">{locale === 'zh' ? '中国' : 'United States'}</span>
		</button>
	)
}
